/**
 * Modal dialog system for confirmations and forms
 * Replaces blocking confirm() calls with modern UI modals
 */

export interface ModalOptions {
    closable?: boolean;
    backdrop?: boolean;
    keyboard?: boolean;
    focus?: boolean;
    type?: string;
    confirmText?: string;
    cancelText?: string;
    submitText?: string;
}

export interface ModalItem {
    id: number;
    element: HTMLElement | null;
    container: HTMLElement | null;
    header: HTMLElement | null;
    body: HTMLElement | null;
    footer: HTMLElement | null;
    options: ModalOptions;
    keyHandler?: (e: KeyboardEvent) => void;
    show?: () => void;
    close?: () => void;
}

export interface FormField {
    name: string;
    label: string;
    type?: string; // 'text' | 'number' | 'textarea' | etc.
    value?: string;
    placeholder?: string;
    required?: boolean;
    validation?: (value: string) => boolean;
}

class ModalDialog {
    activeModals: Set<ModalItem>;
    defaultOptions: ModalOptions;

    constructor() {
        this.activeModals = new Set();
        this.defaultOptions = {
            closable: true,
            backdrop: true,
            keyboard: true,
            focus: true
        };
    }

    /**
     * Show a confirmation dialog
     */
    showConfirmation(
        title: string,
        message: string,
        onConfirm?: () => void,
        onCancel?: () => void,
        options: ModalOptions = {}
    ): ModalItem | null {
        // Handle Node.js environment gracefully
        if (typeof document === 'undefined') {
            if (onConfirm) setTimeout(onConfirm, 0);
            return {
                id: 0,
                element: null,
                container: null,
                header: null,
                body: null,
                footer: null,
                options: {},
                show: () => {},
                close: () => {}
            };
        }

        const modal = this.createModal(title, options);

        // Add message content (guard body)
        if (modal.body) {
            const content = document.createElement('div');
            content.className = 'modal-message';
            content.textContent = message;
            modal.body.appendChild(content);
        }

        // Add action buttons
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'modal-actions';

        const confirmButton = document.createElement('button');
        confirmButton.textContent = options.confirmText || 'OK';
        confirmButton.className = 'modal-btn modal-btn-primary';
        confirmButton.onclick = () => {
            this.close(modal);
            if (onConfirm) onConfirm();
        };

        const cancelButton = document.createElement('button');
        cancelButton.textContent = options.cancelText || 'Cancel';
        cancelButton.className = 'modal-btn modal-btn-secondary';
        cancelButton.onclick = () => {
            this.close(modal);
            if (onCancel) onCancel();
        };

        buttonContainer.appendChild(cancelButton);
        buttonContainer.appendChild(confirmButton);

        if (modal.footer) {
            modal.footer.appendChild(buttonContainer);
        }

        this.show(modal);

        // Focus confirm button by default
        setTimeout(() => {
            try { confirmButton.focus(); } catch (e) { /* ignore */ }
        }, 100);

        return modal;
    }

    /**
     * Show an error dialog
     */
    showError(
        title: string,
        message: string,
        onClose?: () => void,
        options: ModalOptions = {}
    ): ModalItem | null {
        // Handle Node.js environment gracefully
        if (typeof document === 'undefined') {
            if (onClose) setTimeout(onClose, 0);
            return {
                id: 0,
                element: null,
                container: null,
                header: null,
                body: null,
                footer: null,
                options: {},
                show: () => {},
                close: () => {}
            };
        }

        const modal = this.createModal(title, { ...options, type: 'error' });

        if (modal.body) {
            // Add error icon and message
            const content = document.createElement('div');
            content.className = 'modal-content modal-error-content';

            const icon = document.createElement('div');
            icon.className = 'modal-icon modal-icon-error';
            icon.innerHTML = '⚠️';

            const messageEl = document.createElement('div');
            messageEl.className = 'modal-message';
            messageEl.textContent = message;

            content.appendChild(icon);
            content.appendChild(messageEl);
            modal.body.appendChild(content);
        }

        // Add close button
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'modal-actions';

        const closeButton = document.createElement('button');
        closeButton.textContent = 'Close';
        closeButton.className = 'modal-btn modal-btn-primary';
        closeButton.onclick = () => {
            this.close(modal);
            if (onClose) onClose();
        };

        buttonContainer.appendChild(closeButton);
        if (modal.footer) {
            modal.footer.appendChild(buttonContainer);
        }

        this.show(modal);

        // Focus close button
        setTimeout(() => {
            try { closeButton.focus(); } catch (e) { /* ignore */ }
        }, 100);

        return modal;
    }

    /**
     * Show a form dialog
     */
    showForm(
        title: string,
        fields: FormField[],
        onSubmit?: (values: Record<string, string>) => void,
        onCancel?: () => void,
        options: ModalOptions = {}
    ): ModalItem | null {
        const modal = this.createModal(title, options);

        // Create form
        const form = document.createElement('form');
        form.className = 'modal-form';

        const formData: Record<string, HTMLInputElement | HTMLTextAreaElement> = {};
        fields.forEach((field: FormField) => {
            const fieldContainer = document.createElement('div');
            fieldContainer.className = 'modal-field';

            const label = document.createElement('label');
            label.textContent = field.label;
            label.htmlFor = field.name;

            const elTag = field.type === 'textarea' ? 'textarea' : 'input';
            const input = document.createElement(elTag) as HTMLInputElement | HTMLTextAreaElement;

            // If this is an input element, set its type
            if (elTag === 'input') {
                (input as HTMLInputElement).type = (field.type as string) || 'text';
            }

            input.name = field.name;
            input.id = field.name;

            // Set common properties with proper assertions
            const elementValue: string = field.value ?? '';
            (input as HTMLInputElement | HTMLTextAreaElement).value = elementValue;
            (input as HTMLInputElement | HTMLTextAreaElement).placeholder = field.placeholder ?? '';
            if (field.required !== undefined) {
                (input as HTMLInputElement | HTMLTextAreaElement).required = !!field.required;
            }

            if (typeof field.validation === 'function') {
                input.addEventListener('input', () => {
                    const currentValue = (input as HTMLInputElement | HTMLTextAreaElement).value;
                    const isValid = field.validation!(currentValue);
                    (input as HTMLElement).classList.toggle('invalid', !isValid);
                });
            }

            fieldContainer.appendChild(label);
            fieldContainer.appendChild(input);
            form.appendChild(fieldContainer);

            formData[field.name] = input;
        });

        if (modal.body) {
            modal.body.appendChild(form);
        }

        // Add action buttons
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'modal-actions';

        const submitButton = document.createElement('button');
        submitButton.type = 'submit';
        submitButton.textContent = options.submitText || 'Submit';
        submitButton.className = 'modal-btn modal-btn-primary';

        const cancelButton = document.createElement('button');
        cancelButton.type = 'button';
        cancelButton.textContent = options.cancelText || 'Cancel';
        cancelButton.className = 'modal-btn modal-btn-secondary';
        cancelButton.onclick = () => {
            this.close(modal);
            if (onCancel) onCancel();
        };

        form.onsubmit = (e) => {
            e.preventDefault();
            const values: Record<string, string> = {};
            Object.keys(formData).forEach(key => {
                values[key] = (formData[key] as HTMLInputElement | HTMLTextAreaElement).value;
            });

            // Validate all fields
            let isValid = true;
            Object.keys(formData).forEach(key => {
                const field = fields.find(f => f.name === key);
                if (field && field.validation && !field.validation(values[key])) {
                    isValid = false;
                    (formData[key] as HTMLElement).classList.add('invalid');
                }
            });

            if (isValid) {
                this.close(modal);
                if (onSubmit) onSubmit(values);
            }
        };

        buttonContainer.appendChild(cancelButton);
        buttonContainer.appendChild(submitButton);
        if (modal.footer) {
            modal.footer.appendChild(buttonContainer);
        }

        this.show(modal);

        // Focus first input
        const firstInput = form.querySelector('input, textarea') as (HTMLInputElement | HTMLTextAreaElement) | null;
        if (firstInput) {
            setTimeout(() => {
                try { firstInput.focus(); } catch (e) { /* ignore */ }
            }, 100);
        }

        return modal;
    }

    /**
     * Create a modal structure
     */
    createModal(title: string, options: ModalOptions = {}): ModalItem {
        if (typeof document === 'undefined') {
            // For Node.js environment - return object with same shape so callers can access body/footer safely in tests
            return {
                id: 0,
                element: null,
                container: null,
                header: null,
                body: null,
                footer: null,
                options: {},
                show: () => {},
                close: () => {}
            };
        }

        const mergedOptions: ModalOptions = { ...this.defaultOptions, ...options };

        // Create modal overlay
        const overlay = document.createElement('div');
        overlay.className = 'modal-overlay';

        // Create modal container
        const container = document.createElement('div');
        container.className = `modal-container ${mergedOptions.type ? 'modal-' + mergedOptions.type : ''}`;

        // Create modal header
        const header = document.createElement('div');
        header.className = 'modal-header';

        const titleElement = document.createElement('h3');
        titleElement.className = 'modal-title';
        titleElement.textContent = title;

        header.appendChild(titleElement);

        // Create modal body
        const body = document.createElement('div');
        body.className = 'modal-body';

        // Create modal footer
        const footer = document.createElement('div');
        footer.className = 'modal-footer';

        // Prepare close button (do not bind handler yet)
        let closeButton: HTMLButtonElement | null = null;
        if (mergedOptions.closable) {
            closeButton = document.createElement('button');
            closeButton.className = 'modal-close';
            closeButton.innerHTML = '&times;';
            // handler will be attached after modal object exists
            header.appendChild(closeButton);
        }

        // Assemble modal
        container.appendChild(header);
        container.appendChild(body);
        container.appendChild(footer);
        overlay.appendChild(container);

        const modal: ModalItem = {
            id: Date.now() + Math.random(),
            element: overlay,
            container,
            header,
            body,
            footer,
            options: mergedOptions
        };

        // Handle backdrop clicks (attach after modal exists to avoid referencing modal before declaration)
        if (mergedOptions.backdrop) {
            overlay.onclick = (e) => {
                if (e.target === overlay) {
                    this.close(modal);
                }
            };
        }

        // Attach close handler now that modal exists
        if (closeButton) {
            closeButton.onclick = () => {
                this.close(modal);
            };
        }

        // Handle keyboard events
        if (mergedOptions.keyboard) {
            const keyHandler = (e: KeyboardEvent) => {
                if ((e as KeyboardEvent).key === 'Escape') {
                    this.close(modal);
                }
            };
            document.addEventListener('keydown', keyHandler);
            modal.keyHandler = keyHandler;
        }

        return modal;
    }

    /**
     * Show a modal
     */
    show(modal: ModalItem): void {
        if (typeof document === 'undefined') return;

        this.activeModals.add(modal);
        if (modal.element) {
            document.body.appendChild(modal.element);
            // Add show class for animation
            requestAnimationFrame(() => {
                modal.element!.classList.add('modal-show');
            });
        }

        // Prevent body scroll
        document.body.classList.add('modal-open');
    }

    /**
     * Close a modal
     */
    close(modal: ModalItem | null | undefined): void {
        if (!modal || !this.activeModals.has(modal)) return;

        if (modal.element) {
            modal.element.classList.add('modal-hide');
        }

        setTimeout(() => {
            if (modal && modal.element && modal.element.parentNode) {
                modal.element.parentNode.removeChild(modal.element);
            }

            // Remove keyboard handler
            if (modal && modal.keyHandler) {
                document.removeEventListener('keydown', modal.keyHandler);
            }

            if (modal) {
                this.activeModals.delete(modal);
            }

            // Restore body scroll if no more modals
            if (this.activeModals.size === 0) {
                document.body.classList.remove('modal-open');
            }
        }, 300); // Animation duration
    }

    /**
     * Close all modals
     */
    closeAll(): void {
        this.activeModals.forEach(modal => {
            this.close(modal);
        });
    }

    /**
     * Get count of active modals
     */
    getCount(): number {
        return this.activeModals.size;
    }
}

// ES module export
export { ModalDialog };
export default ModalDialog;